import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, AlertCircle, ChevronDown } from 'lucide-react';
import { API_URL } from '../../config/api';

const FormSection = ({ data }) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    role: '',
    blockLocation: '',
    numberOfBlocks: '',
    cleaningFrequency: ''
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setTouched(prev => ({ ...prev, [name]: true }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Please enter your full name';
    }
    
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Please enter your company name';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Please enter your email address';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Please enter your mobile number';
    } else if (!/^(07\d{9}|(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3})$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid UK mobile number (e.g., 07XXX XXXXXX)';
    }
    
    if (!formData.role) {
      newErrors.role = 'Please select your role';
    }
    
    setErrors(newErrors);
    setTouched({
      fullName: true,
      companyName: true,
      email: true,
      phone: true,
      role: true
    });
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      const firstErrorField = document.querySelector('[data-error="true"]');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          company_name: formData.companyName,
          email: formData.email,
          phone: formData.phone,
          role: formData.role || null,
          block_location: formData.blockLocation || null,
          number_of_blocks: formData.numberOfBlocks || null,
          cleaning_frequency: formData.cleaningFrequency || null
        })
      });

      if (response.ok) {
        navigate('/thank-you');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Submission error:', error);
      // Still navigate to thank-you on error to not frustrate user
      navigate('/thank-you');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasError = (fieldName) => errors[fieldName] && touched[fieldName];

  const ErrorMessage = ({ message }) => (
    <div className="flex items-center gap-1.5 mt-2 text-red-600 text-sm">
      <AlertCircle className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
      <span>{message}</span>
    </div>
  );

  const selectClassName = (fieldName) => `
    w-full h-12 sm:h-[52px] px-4 pr-10 rounded-xl text-base 
    appearance-none bg-white cursor-pointer
    border ${hasError(fieldName) ? 'border-red-400' : 'border-gray-200'} 
    focus:outline-none focus:ring-2 focus:ring-offset-0
    ${hasError(fieldName) ? 'focus:ring-red-100 focus:border-red-400' : 'focus:ring-[#1a1f4e]/10 focus:border-[#1a1f4e]'}
    transition-colors
  `;

  return (
    <section id="lead-form" className="px-5 sm:px-6 py-14 md:py-20 bg-[#f3f4f6]">
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-[0_4px_6px_rgba(0,0,0,0.05),0_2px_4px_rgba(0,0,0,0.03)] p-6 sm:p-8 md:p-10 border border-gray-100/80">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-gray-900 text-center mb-2">
            {data.title}
          </h2>
          
          {/* Capacity Scarcity - Subtle Helper Text */}
          <p className="text-gray-400 text-center text-xs mb-3 leading-relaxed">
            We onboard a limited number of new blocks each month to maintain service standards.
          </p>
          
          <p className="text-gray-500 text-center mb-8 text-sm sm:text-base leading-relaxed">
            {data.reassurance}
          </p>
          
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Full Name */}
            <div data-error={hasError('fullName')}>
              <Label htmlFor="fullName" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Full name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full h-12 sm:h-[52px] rounded-xl text-base ${hasError('fullName') ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-[#1a1f4e] focus:ring-[#1a1f4e]/10'}`}
                placeholder="Your full name"
              />
              {hasError('fullName') && <ErrorMessage message={errors.fullName} />}
            </div>
            
            {/* Company Name */}
            <div data-error={hasError('companyName')}>
              <Label htmlFor="companyName" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Company name <span className="text-red-400">*</span>
              </Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full h-12 sm:h-[52px] rounded-xl text-base ${hasError('companyName') ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-[#1a1f4e] focus:ring-[#1a1f4e]/10'}`}
                placeholder="Your company name"
              />
              {hasError('companyName') && <ErrorMessage message={errors.companyName} />}
            </div>
            
            {/* Email */}
            <div data-error={hasError('email')}>
              <Label htmlFor="email" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Email address <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full h-12 sm:h-[52px] rounded-xl text-base ${hasError('email') ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-[#1a1f4e] focus:ring-[#1a1f4e]/10'}`}
                placeholder="you@company.com"
              />
              {hasError('email') && <ErrorMessage message={errors.email} />}
            </div>
            
            {/* Phone */}
            <div data-error={hasError('phone')}>
              <Label htmlFor="phone" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Mobile number <span className="text-red-400">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className={`w-full h-12 sm:h-[52px] rounded-xl text-base ${hasError('phone') ? 'border-red-400 focus:border-red-400 focus:ring-red-100' : 'border-gray-200 focus:border-[#1a1f4e] focus:ring-[#1a1f4e]/10'}`}
                placeholder="07XXX XXXXXX"
              />
              {hasError('phone') && <ErrorMessage message={errors.phone} />}
            </div>
            
            {/* Role - Native Select */}
            <div data-error={hasError('role')}>
              <Label htmlFor="role" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Your role <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleSelectChange}
                  onBlur={handleBlur}
                  className={selectClassName('role')}
                >
                  <option value="" disabled>Select your role</option>
                  {data.roleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
              {hasError('role') && <ErrorMessage message={errors.role} />}
            </div>
            
            {/* Block Location */}
            <div>
              <Label htmlFor="blockLocation" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Block location (postcode or area)
              </Label>
              <Input
                id="blockLocation"
                name="blockLocation"
                type="text"
                value={formData.blockLocation}
                onChange={handleInputChange}
                className="w-full h-12 sm:h-[52px] rounded-xl text-base border-gray-200 focus:border-[#1a1f4e] focus:ring-[#1a1f4e]/10"
                placeholder="e.g., HA8 or Edgware"
              />
            </div>
            
            {/* Number of Blocks - Native Select */}
            <div>
              <Label htmlFor="numberOfBlocks" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Number of blocks
              </Label>
              <div className="relative">
                <select
                  id="numberOfBlocks"
                  name="numberOfBlocks"
                  value={formData.numberOfBlocks}
                  onChange={handleSelectChange}
                  className={selectClassName('')}
                >
                  <option value="" disabled>Select number of blocks</option>
                  {data.blockCountOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            {/* Cleaning Frequency - Native Select */}
            <div>
              <Label htmlFor="cleaningFrequency" className="text-gray-700 font-medium mb-2 block text-sm sm:text-base">
                Cleaning frequency needed
              </Label>
              <div className="relative">
                <select
                  id="cleaningFrequency"
                  name="cleaningFrequency"
                  value={formData.cleaningFrequency}
                  onChange={handleSelectChange}
                  className={selectClassName('')}
                >
                  <option value="" disabled>Select frequency</option>
                  {data.frequencyOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>
            
            {/* Submit Section */}
            <div className="pt-4">
              {/* Privacy Trust Line */}
              <p className="text-center text-gray-400 text-xs mb-4">
                We only use your details to arrange your site assessment.
              </p>
              
              {/* Time Expectation Helper */}
              <p className="text-center text-gray-400 text-xs mb-3">
                Most site assessments take 10–15 minutes.
              </p>
              
              <Button
                type="submit"
                disabled={isSubmitting}
                data-testid="form-submit-btn"
                className="w-full bg-[#00c853] hover:bg-[#00a844] text-white h-14 sm:h-[58px] min-h-[48px] text-base sm:text-lg font-medium rounded-xl transition-all duration-200 shadow-[0_2px_8px_rgba(0,200,83,0.25)] hover:shadow-[0_4px_12px_rgba(0,200,83,0.35)] hover:-translate-y-0.5 active:translate-y-0"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Request Free Site Assessment'
                )}
              </Button>
              
              {/* Post-submit Expectation */}
              <p className="text-center text-gray-500 text-sm mt-4">
                We'll contact you within 1 working day.
              </p>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default FormSection;
